import * as React from "react"
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs"
import type { RendererProps } from "../core/element-registry"
import { useRenderContext } from "../core/render-context"
import { useAtomDefaults } from "../contexts/atom-defaults-context"
import { useBoundValue } from "../hooks/use-bound-value"
import { cn } from "../lib/utils"
import { resolveIcon } from "./resolve-icon"
import { useResolveCls } from "../contexts/breakpoint-context"
import { ElementRenderer, liftV4Props } from "./element-renderer"
import type { CogoElement } from "../core/types"
import { readResolvedArray, recordValue, resolvedText, shorthandId } from "./property-value-utils"

export function TabsRenderer({ element }: RendererProps) {
  const { dispatchEvent, resolveSymbol, navigate } = useRenderContext()
  const rcls = useResolveCls()
  // Theme-v2 atom defaults feed the fallback chain:
  //   element.variant/size → atomDefaults.default* → shadcn "default"
  const atomDefaults = useAtomDefaults("tabs")
  const resolvedVariant = (element.variant as string | undefined)
    ?? atomDefaults.defaultVariant
    ?? "default"
  const resolvedSize = (element as { size?: string }).size
    ?? atomDefaults.defaultSize
    ?? "default"

  // Support both flat children (legacy) and nested tabs-list/tabs-content structure (v2)
  // JITRULE Task 2: authored children skip the recordValue() shorthand path
  // below, so lift them directly — same rule, single interpreter.
  const explicitChildren = (element.children ?? []).map(liftV4Props)
  const shorthandTabs = explicitChildren.length === 0
    ? readResolvedArray(element, "tabs", resolveSymbol).map((raw, index) => {
        const tab = recordValue(raw)
        const id = String(tab.id ?? shorthandId(element.id, "tabs", index))
        const value = String(tab.value ?? id)
        const rawContent = tab.content
        const contentChildren: CogoElement[] = Array.isArray(rawContent)
          ? rawContent.map((entry, contentIndex) => {
              const child = recordValue(entry)
              return {
                ...child,
                id: String(child.id ?? shorthandId(id, "content", contentIndex)),
                type: String(child.type ?? "text"),
                text: child.text ?? child.label,
              } as unknown as CogoElement
            })
          : [{ id: `${id}__content_text`, type: "text", text: resolvedText(rawContent, resolveSymbol) }]
        return {
          trigger: {
            ...tab,
            id: `${id}__trigger`,
            type: "tabs-trigger",
            text: tab.label ?? tab.text,
            value,
          } as unknown as CogoElement,
          content: {
            id: `${id}__content`,
            type: "tabs-content",
            value,
            children: contentChildren,
          } as CogoElement,
        }
      })
    : []
  const rawChildren: CogoElement[] = explicitChildren.length > 0
    ? explicitChildren
    : shorthandTabs.length > 0
      ? [
          { id: `${element.id}__tabs_list`, type: "tabs-list", children: shorthandTabs.map((tab) => tab.trigger) },
          ...shorthandTabs.map((tab) => tab.content),
        ]
      : []
  const tabsListEl = rawChildren.find(c => c.type === "tabs-list")
  // tabsListEl.children is one level deeper than rawChildren (already
  // lifted above when it came from explicitChildren) — lift again here.
  const tabs = (tabsListEl?.children ?? rawChildren).map(liftV4Props)
  const tabContents = rawChildren.filter(c => c.type === "tabs-content")
  const rawBindRoute = (element as unknown as { bindRoute?: unknown }).bindRoute
  const resolvedBindRoute = typeof rawBindRoute === "string"
    ? resolveSymbol(rawBindRoute)
    : rawBindRoute
  const bindRoute = resolvedBindRoute === true || resolvedBindRoute === "true"
  const variant = element.variant as string | undefined

  const resolvedTabs = tabs.map((child) => {
    const tabValue = child.value ?? child.id
    const rawIconName = typeof child.icon === "string" ? child.icon : ""
    const iconName = rawIconName.includes("#{")
      ? String(resolveSymbol(rawIconName) ?? "")
      : rawIconName
    const IconComponent = iconName ? resolveIcon(iconName) : undefined
    const text = child.text
      ? String(resolveSymbol(child.text) ?? child.text)
      : ""

    return {
      child,
      tabValue,
      iconName,
      IconComponent,
      text,
      isVisible: text.trim().length > 0 || Boolean(IconComponent),
    }
  })
  const visibleTabs = resolvedTabs.filter((tab) => tab.isVisible)
  const fallbackTabs = visibleTabs.length > 0 ? visibleTabs : resolvedTabs
  const rawDefaultIndex = (element as unknown as { defaultIndex?: unknown }).defaultIndex
  const hasAuthoredDefaultIndex = rawDefaultIndex !== undefined && rawDefaultIndex !== null
  const resolvedDefaultIndex = typeof rawDefaultIndex === "string"
    ? resolveSymbol(rawDefaultIndex)
    : rawDefaultIndex
  const numericDefaultIndex = Number(resolvedDefaultIndex ?? 0)
  const defaultIndex = Math.min(
    Math.max(0, fallbackTabs.length - 1),
    Number.isFinite(numericDefaultIndex) ? Math.max(0, Math.floor(numericDefaultIndex)) : 0,
  )

  // State binding via useBoundValue (1-cycle sync, no useEffect)
  const [boundVal, setBoundVal] = useBoundValue<string>(
    element.bind,
    resolveSymbol,
    "",
    String,
  )

  // Route binding via useBoundValue (1-cycle sync, no useEffect)
  const [routeVal] = useBoundValue<string>(
    bindRoute ? "_system.navigation.currentRoute" : undefined,
    resolveSymbol,
    "/",
    String,
  )

  // Priority: bound state > route > explicit default > active variant > first tab
  // Note: boundVal can be "" (empty string) which is a valid sync target (SYNC-03 bug fix)
  const explicitDefault = element.defaultValue as string | undefined
  const value =
    (element.bind !== undefined ? boundVal : undefined)
    ?? (bindRoute ? routeVal : undefined)
    ?? explicitDefault
    ?? (hasAuthoredDefaultIndex ? fallbackTabs[defaultIndex]?.tabValue : undefined)
    ?? fallbackTabs.find(t => t.child.variant === "active")?.tabValue
    ?? fallbackTabs[0]?.tabValue
    ?? "0"

  const handleValueChange = (newValue: string) => {
    setBoundVal(newValue)
    if (bindRoute && navigate) {
      navigate(newValue)
    }
    dispatchEvent(element.id, "change", { value: newValue })
  }

  // If tabs-list has its own className, use it; otherwise let shadcn TabsList defaults apply
  const listClasses = tabsListEl?.className
    ? rcls(tabsListEl.className)
    : undefined

  // Base UI expects at least one trigger whose value can own the controlled
  // tab state. Keep an incomplete/generated tabs tree renderable instead of
  // feeding it an impossible value/content combination that update-loops.
  if (visibleTabs.length === 0) {
    return (
      <div
        className={cn("cn-tabs group/tabs flex flex-col", element.className)}
        data-atom-type="tabs"
        data-variant={resolvedVariant}
        data-size={resolvedSize}
        data-bind-route={bindRoute}
        data-default-index={defaultIndex}
      >
        {tabsListEl ? (
          <div
            className={cn("cn-tabs-list", listClasses)}
            data-cogo-element-id={tabsListEl.id}
            data-cogo-type={tabsListEl.type}
          />
        ) : null}
        {tabContents.flatMap((content) =>
          content.children?.map((child) => (
            <ElementRenderer key={child.id} element={child} />
          )) ?? [],
        )}
      </div>
    )
  }

  return (
    <Tabs
      {...(element.bind !== undefined
        ? { value, onValueChange: handleValueChange }
        : { defaultValue: value, onValueChange: handleValueChange }
      )}
      className={cn("cn-tabs group/tabs flex flex-col", element.className)}
      data-atom-type="tabs"
      data-variant={resolvedVariant}
      data-size={resolvedSize}
      data-bind-route={bindRoute}
      data-default-index={defaultIndex}
      data-active-value={value}
    >
      {visibleTabs.length > 0 && (
        <TabsList
          variant={variant === "line" ? "line" : "default"}
          className={cn(listClasses)}
          data-cogo-element-id={tabsListEl?.id}
          data-cogo-type={tabsListEl?.type}
        >
          {visibleTabs.map(({ child, tabValue, iconName, IconComponent, text }) => {
            const isActive = value === tabValue

            return (
              <TabsTrigger
                key={child.id}
                value={tabValue}
                className={cn(rcls(child.className))}
                data-route={tabValue}
                data-active={isActive || undefined}
                data-cogo-element-id={child.id}
                data-cogo-type={child.type}
                data-icon={IconComponent ? iconName : undefined}
                disabled={child.disabled === true}
              >
                {IconComponent && React.createElement(IconComponent, { className: "size-4" })}
                {text}
              </TabsTrigger>
            )
          })}
        </TabsList>
      )}
      {tabContents.map((content) => {
        const contentValue = content.value ?? content.id ?? ""
        return (
          <TabsContent
            key={content.id}
            value={contentValue}
            className={cn(content.className)}
            data-cogo-element-id={content.id}
            data-cogo-type={content.type}
          >
            {content.children?.map((child) => (
              <ElementRenderer key={child.id} element={child} />
            ))}
          </TabsContent>
        )
      })}
    </Tabs>
  )
}
